/**
 * IndexedDB database `mdt` (roadmap 3.3): `drafts` holds encrypted form drafts, `keys` holds the
 * standard-mode draft key as a non-extractable CryptoKey (structured clone keeps it non-extractable), so
 * drafts can be decrypted after a reload while offline. Shared mode never writes `keys`.
 *
 * Every connection closes itself on `versionchange`, so a wipe (deleteDatabase) in one tab is never
 * blocked by another tab holding the database open.
 */
export const DB_NAME = 'mdt';
const DB_VERSION = 1;

export type DraftRecord = {
  /** `${personId}|${formKey}|${entityId}` */
  id: string;
  personId: string;
  formKey: string;
  entityId: string;
  keyId: string;
  iv: Uint8Array;
  ciphertext: ArrayBuffer;
  schemaVersion: number;
  updatedAt: number;
};

export type KeyRecord = { personId: string; keyId: string; key: CryptoKey };

let dbPromise: Promise<IDBDatabase> | null = null;

function openOnce(): Promise<IDBDatabase> {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('drafts')) db.createObjectStore('drafts', { keyPath: 'id' }).createIndex('personId', 'personId');
      if (!db.objectStoreNames.contains('keys')) db.createObjectStore('keys', { keyPath: 'personId' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('indexedDB open failed'));
  });
}

export function openDb(): Promise<IDBDatabase> {
  dbPromise ??= (async () => {
    let db = await openOnce();
    if (!db.objectStoreNames.contains('drafts') || !db.objectStoreNames.contains('keys')) {
      // An empty `mdt` at this version (made by something else) would never be upgraded: start it over.
      db.close();
      await new Promise<void>((done) => {
        const del = indexedDB.deleteDatabase(DB_NAME);
        del.onsuccess = del.onerror = del.onblocked = () => done();
      });
      db = await openOnce();
    }
    db.onversionchange = () => {
      db.close();
      dbPromise = null;
    };
    return db;
  })().catch((e: unknown) => {
    dbPromise = null;
    throw e;
  });
  return dbPromise;
}

function run<T>(store: 'drafts' | 'keys', mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then((db) => new Promise<T>((resolve, reject) => {
    const tx = db.transaction(store, mode);
    const req = fn(tx.objectStore(store));
    let result: T;
    req.onsuccess = () => {
      result = req.result;
    };
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error ?? req.error);
    tx.onabort = () => reject(tx.error ?? new Error('transaction aborted'));
  }));
}

export const draftId = (personId: string, formKey: string, entityId: string) => `${personId}|${formKey}|${entityId}`;

export const drafts = {
  put: (r: DraftRecord) => run('drafts', 'readwrite', (s) => s.put(r)).then(() => undefined),
  get: (id: string) => run<DraftRecord | undefined>('drafts', 'readonly', (s) => s.get(id)),
  delete: (id: string) => run('drafts', 'readwrite', (s) => s.delete(id)).then(() => undefined),
  all: () => run<DraftRecord[]>('drafts', 'readonly', (s) => s.getAll()),
};

export const keys = {
  put: (r: KeyRecord) => run('keys', 'readwrite', (s) => s.put(r)).then(() => undefined),
  get: (personId: string) => run<KeyRecord | undefined>('keys', 'readonly', (s) => s.get(personId)),
  delete: (personId: string) => run('keys', 'readwrite', (s) => s.delete(personId)).then(() => undefined),
  all: () => run<KeyRecord[]>('keys', 'readonly', (s) => s.getAll()),
};

export async function closeDb(): Promise<void> {
  const p = dbPromise;
  dbPromise = null;
  if (p) (await p.catch(() => null))?.close();
}

/** Deletes the whole database. Resolves even if some other context keeps it blocked (then it is deleted
 *  as soon as that context lets go; what remains meanwhile is ciphertext under a destroyed key). */
export async function deleteDb(): Promise<void> {
  await closeDb();
  await new Promise<void>((resolve) => {
    const req = indexedDB.deleteDatabase(DB_NAME);
    const done = () => resolve();
    req.onsuccess = done;
    req.onerror = done;
    req.onblocked = () => setTimeout(done, 1500);
  });
}
